/**
 * Utility functions for building API query parameters
 */

export interface QueryParams {
  search?: string
  page?: number
  page_size?: number
  [key: string]: string | number | boolean | undefined
}

/**
 * Builds URLSearchParams from an object of query parameters
 * Filters out undefined values and converts numbers to strings
 *
 * @param params - Object containing query parameters (can be undefined or null)
 * @returns URLSearchParams instance
 *
 * @example
 * ```ts
 * const params = buildQueryParams({
 *   search: 'test',
 *   page: 1,
 *   page_size: 10,
 *   provider: 'openai'
 * });
 * // Returns: URLSearchParams with search=test&page=1&page_size=10&provider=openai
 * ```
 */
export function buildQueryParams(params?: QueryParams | null): URLSearchParams {
  const searchParams = new URLSearchParams()

  if (!params) {
    return searchParams
  }

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, value.toString())
    }
  })

  return searchParams
}

/**
 * Builds a query string from query parameters
 * Returns empty string if no params, otherwise returns `?key=value&...`
 *
 * @param params - Object containing query parameters (can be undefined or null)
 * @returns Query string (e.g., "?search=test&page=1" or "")
 *
 * @example
 * ```ts
 * const query = buildQueryString({ search: 'test', page: 1 });
 * // Returns: "?search=test&page=1"
 * ```
 */
export function buildQueryString(params?: QueryParams | null): string {
  const searchParams = buildQueryParams(params)
  const query = searchParams.toString()
  return query ? `?${query}` : ''
}
